/**
 * @description Candy Cart specific requests
 */

//@ts-ignore
import { OffersResponseBody } from '../types';
import { handleResponseDeprecated, parseJson } from '../shared/api/utils';
import { Offer, OfferSessionStorage } from '../shared/types/Offer';
import getLanguage from '../shared/utils/getLanguage';
import { customSessionStorage } from '../shared/product-page-placement/storage';
import { TRACKING_KEY } from '../shared/product-page-placement/tracking-manager';
import { updateAttributes } from '../shared/api/shared-app-requests';
import { logError } from '../shared/utils/logging';
import captureException from '../utils/sentry';
import { appFunctions } from '../utils/app-utils';

import { Cart } from '@digismoothie/shared-candyrack';
import { REGISTERED_APPS } from '../shared/types';

const REQUEST_HEADERS: RequestInit = {
  method: 'POST',
  mode: 'cors',
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'x-shop': window.Shopify.shop,
  } as HeadersInit,
};

/*
 ** Only relevant for CC (forked app) path
 */
async function createDraftOrderForCandyCart(
  cart: Cart,
  offers: OfferSessionStorage[],
) {
  const hostname = process.env.REACT_APP_LAST_UPSELL_API_HOSTNAME;
  const data = {
    note: cart.note,
    cart: {
      cart_total_price_cents: cart.total_price,
      cart_items: cart.items,
      cart_token: cart.token,
      discount_code: cart.total_discount,
      currency: window.Shopify.currency.active,
    },
    offers: offers.map((offerSessionStorage) => ({
      offer_id: offerSessionStorage.id,
      variant_id: offerSessionStorage.shopify_product_variant_id,
      quantity: offerSessionStorage.quantity,
      note: offerSessionStorage.note,
    })),
  };
  const response = await handleResponseDeprecated(
    fetch(`${hostname}api/candycart/draft-order`, {
      ...REQUEST_HEADERS,
      body: JSON.stringify(data),
    }),
  );
  return response;
}

async function getOffers(cart: Cart) {
  const data = {
    cart_items: cart.items,
    cart_total_price_cents: cart.total_price,
    cart_token: cart.token,
    currency: cart.currency,
    lang: getLanguage() || '',
  };

  let response: OffersResponseBody | undefined = undefined;
  const requestInfo: RequestInfo = appFunctions.isBaseApp()
    ? `${process.env.REACT_APP_CUSTOMER_API_HOSTNAME}api/candyrack/checkout/offers`
    : `${process.env.REACT_APP_LAST_UPSELL_API_HOSTNAME}api/candycart/offers`;
  try {
    response = await parseJson(
      fetch(requestInfo, {
        ...REQUEST_HEADERS,
        body: JSON.stringify(data),
      }),
    );
  } catch (error) {
    logError(
      'Error while getting offers in candycart.ts',
      REGISTERED_APPS.CANDYCART,
      error,
    );
    captureException(
      error instanceof Error
        ? error
        : new Error(
            `Error while getting offers in candycart.ts, response: ${response}`,
          ),
    );
  }
  return response as OffersResponseBody;
}

async function trackAddToCart(offer: Offer, cart: Cart) {
  let addToCartHandleData = offer.shopifyProductHandleComputedInFE;

  // in case of service the handle remains empty, and we need to use the item_id
  if (!addToCartHandleData) {
    const itemAddedToCart = offer.items.find((item) =>
      item.item_variants.some(
        (itemVariant) =>
          itemVariant === offer.shopifyProductVariantIdComputedInFE,
      ),
    );
    if (itemAddedToCart) {
      addToCartHandleData = itemAddedToCart.item_id.toString();
    }
  }
  const data = {
    cart_token: cart.token,
    offer_id: offer.id,
    shopify_product_variant_id: offer.shopifyProductVariantIdComputedInFE,
    quantity: offer.quantityComputedInFE,
    handle: addToCartHandleData,
  };
  if (appFunctions.isBaseApp()) {
    let token = customSessionStorage.getItem(TRACKING_KEY);
    if (!token) {
      token = cart.token;
      customSessionStorage.setItem(TRACKING_KEY, cart.token);
    }
    await updateAttributes({
      candyrack_token: token,
    });

    // Update cart token
    data.cart_token = token;

    if (offer.is_same_product_offer) {
      const parentProduct = cart.items.find(
        (cartItem) =>
          cartItem.handle === offer.shopifyProductHandleComputedInFE,
      );
      // @ts-ignore
      data.parent_product = {
        id: parentProduct?.product_id,
        handle: parentProduct?.handle,
      };
    }
  }

  const response = await handleResponseDeprecated(
    fetch(
      appFunctions.isBaseApp()
        ? `${process.env.REACT_APP_CUSTOMER_API_HOSTNAME}api/candyrack/checkout/track/add-to-cart`
        : `${process.env.REACT_APP_LAST_UPSELL_API_HOSTNAME}api/candycart/track/add-to-cart`,
      {
        ...REQUEST_HEADERS,
        body: JSON.stringify(data),
      },
    ),
  );
  return response;
}

export { createDraftOrderForCandyCart, getOffers, trackAddToCart };
